<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';
import { computed } from 'vue';
import compromissosAtrasados from '~/graphql/pre-defined-filters/compromissosAtrasados';
import compromissosConcluidos from '~/graphql/pre-defined-filters/compromissosConcluidos';
import compromissosVencimentoHoje from '~/graphql/pre-defined-filters/compromissosVencimentoHoje';
import compromissosVencimentoProximosDias from '~/graphql/pre-defined-filters/compromissosVencimentoProximosDias';
import pastasAcordosNoMes from '~/graphql/pre-defined-filters/pastasAcordosNoMes';
import pastasAtivas from '~/graphql/pre-defined-filters/pastasAtivas';
import pastasGcpjDesatualizado from '~/graphql/pre-defined-filters/pastasGcpjDesatualizado';
import publicacoesHoje from '~/graphql/pre-defined-filters/publicacoesHoje';
import { formatDinheiro } from '~/shared/utils/helpers';
import { useDatatableDrawerStore } from '~/stores/datatableStore';

interface Data {
  key: string;
  value: any;
}

interface Props {
  responsavel: string;
  data: Data[];
}

const props = withDefaults(defineProps<Props>(), {});

const datatableDrawerStore = useDatatableDrawerStore();

// PASTAS ATIVAS ULTIMOS MESES
const pastasAtivasUltimosMeses = computed(() => {
  return Object.entries(
    props.data.find(
      (x) => x.key === `${props.responsavel}-totalPastasAtivasUltimosMeses`,
    )?.value ?? [],
  ).sort(([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  });
});

const pastasAtivasUltimosMesesCategories = computed(() => {
  return pastasAtivasUltimosMeses.value.map(([x, _]) => x.split('|')[0]);
});

const pastasAtivasUltimosMesesSeries = computed(() => {
  return [
    {
      name: 'Total de pastas',
      data: pastasAtivasUltimosMeses.value.map(([_, x]) => x as number),
    },
  ];
});

// VALORES CAUSAS ULTIMOS MESES
const valoresCausasUltimosMeses = computed(() => {
  return Object.entries(
    props.data.find(
      (x) => x.key === `${props.responsavel}-totalValoresCausasUltimosMeses`,
    )?.value ?? [],
  ).sort(([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  });
});

const valoresCausasUltimosMesesCategories = computed(() => {
  return valoresCausasUltimosMeses.value.map(([x, _]) => x.split('|')[0]);
});

const valoresCausasUltimosMesesSeries = computed(() => {
  return [
    {
      name: 'Valores das causas',
      data: valoresCausasUltimosMeses.value.map(([_, x]) => x as number),
    },
  ];
});

// TOTAL ACORDOS ULTIMOS MESES
const totalAcordosUltimosMeses = computed(() => {
  return Object.entries(
    props.data.find(
      (x) => x.key === `${props.responsavel}-totalAcordosUltimosMeses`,
    )?.value ?? [],
  ).sort(([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  });
});

const totalAcordosUltimosMesesCategories = computed(() => {
  return totalAcordosUltimosMeses.value.map(([x, _]) => x.split('|')[0]);
});

const totalAcordosUltimosMesesSeries = computed(() => {
  return [
    {
      name: 'Total de acordos',
      data: totalAcordosUltimosMeses.value.map(([_, x]) => x as number),
    },
  ];
});

// TOTAL PUBLICACOES ULTIMOS DIAS
const totalPublicacoesUltimosDias = computed(() => {
  return Object.entries(
    props.data.find(
      (x) => x.key === `${props.responsavel}-totalPublicacoesUltimosDias`,
    )?.value ?? [],
  ).sort(([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  });
});

const totalPublicacoesUltimosDiasCategories = computed(() => {
  return totalPublicacoesUltimosDias.value.map(([x, _]) => x.split('|')[0]);
});

const totalPublicacoesUltimosDiasSeries = computed(() => {
  return [
    {
      name: 'Total de publicações',
      data: totalPublicacoesUltimosDias.value.map(([_, x]) => x as number),
    },
  ];
});

// PASTAS POR STATUS
const pastasAgrupadasPorStatus = computed(() => {
  return Object.entries(
    props.data.find(
      (x) => x.key === `${props.responsavel}-totalPastasAgrupadasPorStatus`,
    )?.value ?? [],
  ).sort(([_, valA], [__, valB]) => {
    return (valB as number) - (valA as number);
  });
});
const queryClient = useQueryClient();

function onDatatableDrawerClose() {
  queryClient.invalidateQueries({
    queryKey: [props.responsavel],
    exact: true,
  });
}
</script>
<template>
  <h2 style="margin-top: 12px">Compromissos</h2>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="6">
      <Stats
        title="EM ATRASO"
        :clickable="true"
        :value="
          props.data.find(
            (x) => x.key === `${props.responsavel}-totalCompromissosEmAtraso`,
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'CompromissoGqlDatatable',
              title: 'COMPROMISSOS EM ATRASO',
              variables: compromissosAtrasados({
                responsaveis: [props.responsavel!],
              }),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="6">
      <Stats
        title="VENCIMENTO HOJE"
        :clickable="true"
        :value="
          props.data.find(
            (x) =>
              x.key === `${props.responsavel}-totalCompromissosVencimentoHoje`,
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'CompromissoGqlDatatable',
              title: 'COMPROMISSOS COM VENCIMENTO HOJE',
              variables: compromissosVencimentoHoje({
                responsaveis: [props.responsavel!],
              }),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="6">
      <Stats
        title="VENCIMENTO PRÓXIMOS DIAS"
        :clickable="true"
        :value="
          props.data.find(
            (x) =>
              x.key ===
              `${props.responsavel}-totalCompromissosVencimentoProximosDias`,
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'CompromissoGqlDatatable',
              title: 'COMPROMISSOS COM VENCIMENTO NOS PRÓXIMOS DIAS',
              variables: compromissosVencimentoProximosDias({
                responsaveis: [props.responsavel!],
              }),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="6">
      <Stats
        title="CONCLUÍDOS"
        :clickable="true"
        :value="
          props.data.find(
            (x) => x.key === `${props.responsavel}-totalCompromissosConcluidos`,
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'CompromissoGqlDatatable',
              title: 'COMPROMISSOS CONCLUÍDOS',
              variables: compromissosConcluidos({
                responsaveis: [props.responsavel],
              }),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
  </a-row>

  <h2 style="margin-top: 24px">Pastas</h2>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="6">
      <Stats
        title="PASTAS ATIVAS"
        :clickable="true"
        :value="
          props.data.find(
            (x) => x.key === `${props.responsavel}-totalPastasAtivas`,
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'PastaGqlDatatable',
              title: 'PASTAS ATIVAS',
              variables: pastasAtivas({
                responsaveis: [props.responsavel],
              }),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="6">
      <Stats
        title="GCPJ DESATUALIZADO"
        :clickable="true"
        :value="
          props.data.find(
            (x) =>
              x.key === `${props.responsavel}-totalPastasGcpjsDesatualizados`,
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'PastaGqlDatatable',
              title: 'PASTAS COM GCPJ DESATUALIZADO',
              variables: pastasGcpjDesatualizado({
                responsaveis: [props.responsavel],
              }),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="6">
      <Stats
        title="ACORDOS NO MÊS"
        :clickable="true"
        :value="
          props.data.find(
            (x) => x.key === `${props.responsavel}-totalAcordosNoMes`,
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'PastaGqlDatatable',
              title: 'PASTAS COM ACORDOS NO MÊS',
              variables: pastasAcordosNoMes({
                responsaveis: [props.responsavel],
              }),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
    <a-col :span="6">
      <Stats
        title="PUBLICAÇÕES HOJE"
        :clickable="true"
        :value="
          props.data.find(
            (x) => x.key === `${props.responsavel}-totalPublicacoesHoje`,
          )?.value
        "
        @click="
          () =>
            datatableDrawerStore.open({
              componentName: 'PublicacaoNumeroProcessoGqlDatatable',
              title: 'PUBLICAÇÕES HOJE',
              variables: publicacoesHoje({
                responsaveis: [props.responsavel],
              }),
              onClose: onDatatableDrawerClose,
            })
        "
      ></Stats>
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="12">
      <LineChart
        title="PASTAS ATIVAS"
        :categories="pastasAtivasUltimosMesesCategories"
        :series="pastasAtivasUltimosMesesSeries"
      />
    </a-col>
    <a-col :span="12">
      <LineChart
        title="VALORES CAUSAS"
        :categories="valoresCausasUltimosMesesCategories"
        :series="valoresCausasUltimosMesesSeries"
        :value-formater="(value: any) => formatDinheiro(value)"
      />
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="12">
      <Stats
        title="VALORES CAUSAS"
        :value="
          formatDinheiro(
            props.data.find(
              (x) => x.key === `${props.responsavel}-totalValoresCausas`,
            )?.value,
          )
        "
      />
    </a-col>
    <a-col :span="12">
      <Stats
        title="VALOR ACORDO NO MÊS"
        :value="
          formatDinheiro(
            props.data.find(
              (x) => x.key === `${props.responsavel}-totalValoresAcordosNoMes`,
            )?.value,
          )
        "
      />
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="12">
      <LineChart
        title="TOTAL ACORDOS"
        :categories="totalAcordosUltimosMesesCategories"
        :series="totalAcordosUltimosMesesSeries"
      />
    </a-col>
    <a-col :span="12">
      <LineChart
        title="PUBLICAÇÕES"
        :categories="totalPublicacoesUltimosDiasCategories"
        :series="totalPublicacoesUltimosDiasSeries"
      />
    </a-col>
  </a-row>

  <a-row :gutter="24" style="margin-top: 24px">
    <a-col :span="12">
      <BarChart
        title="PASTAS POR STATUS"
        :categories="pastasAgrupadasPorStatus.map(([x, _]) => x)"
        :series="[
          {
            name: 'Total de pastas',
            data: pastasAgrupadasPorStatus.map(([_, x]) => x as number),
          },
        ]"
      />
    </a-col>
  </a-row>
</template>
